import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { Button, type ButtonSize } from './button'

/**
 * Visual weight of the confirm button. `'danger'` paints it deep red
 * (design-guideline §7.2「破壞性」), reserved for an action that destroys work
 * the user cannot get back. Defaults to `'default'` (ink black).
 */
export type KiaoConfirmTone = 'default' | 'danger'

export interface KiaoConfirmOptions {
  title: string
  message?: string
  confirmText: string
  cancelText: string
  /** Which button receives initial focus. Defaults to `'cancel'` — the safe
   *  default for destructive actions (Enter won't fire the confirm). Pass
   *  `'confirm'` for non-destructive confirmations (e.g. merge speakers). */
  defaultFocus?: 'cancel' | 'confirm'
  /**
   * Defaults to `'default'` (ink black) — deliberately, and not merely for
   * backwards compatibility: red is a budgeted signal, and a dialog that shouts
   * at every confirmation teaches people to click through the shouting. Reach
   * for it only where the copy alone would under-sell the loss.
   */
  tone?: KiaoConfirmTone
}

type ConfirmFn = (options: KiaoConfirmOptions) => Promise<boolean>

const KiaoConfirmContext = createContext<ConfirmFn | null>(null)

interface PendingConfirm {
  options: KiaoConfirmOptions
  resolve: (result: boolean) => void
}

/** 通用確認對話框（.kiao-dialog）。
 *  用法：const confirm = useKiaoConfirm(); await confirm({...}) → boolean。
 *  確認 true；取消、Esc、點背景 false。破壞性操作預設聚焦「取消」。
 *  警示預設由文案承擔（確認鈕墨黑 primary）；真正會毀掉拿不回來的東西時，
 *  呼叫端傳 `tone: 'danger'` 才上深紅（design-guideline §7.2）。 */
export interface KiaoConfirmProviderProps {
  children: React.ReactNode
  /**
   * Size of both dialog buttons. A button's height is set by its size, never by
   * where it sits (design-guideline §7.2), so a desktop window that uses the
   * 36px mouse row passes `'row'` here. Default `'md'`.
   */
  buttonSize?: ButtonSize
}

export function KiaoConfirmProvider({ children, buttonSize = 'md' }: KiaoConfirmProviderProps) {
  const [pending, setPending] = useState<PendingConfirm | null>(null)
  const pendingQueueRef = useRef<PendingConfirm[]>([])
  const dialogRef = useRef<HTMLDialogElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        const next = { options, resolve }
        pendingQueueRef.current.push(next)
        // Keep the displayed item stable while other callers enqueue within
        // the same event. Each promise is settled FIFO in `settle` below.
        setPending(pendingQueueRef.current[0] ?? null)
      }),
    []
  )

  // Layout effect, not passive: see `kiao-modal.tsx` — the `<dialog>` is
  // `display: none`, so its buttons stay out of the accessibility tree until
  // this runs, and that has to happen in the same commit that renders them.
  useLayoutEffect(() => {
    if (!pending) return
    const dialog = dialogRef.current
    if (dialog && !dialog.open) {
      if (typeof dialog.showModal === 'function') dialog.showModal()
      else dialog.setAttribute('open', '')
    }
    const target =
      pending.options.defaultFocus === 'confirm' ? confirmRef.current : cancelRef.current
    target?.focus({ preventScroll: true })
  }, [pending])

  const settle = useCallback((result: boolean, activationCount = 1) => {
    // Buttons stay mounted while queue content changes, so browser click
    // counts identify a double-click even when its second click lands at the
    // same position on the next confirmation. Never turn that second click
    // into consent for a different request.
    if (activationCount > 1) return
    const current = pendingQueueRef.current.shift()
    if (!current) return
    current.resolve(result)
    const next = pendingQueueRef.current[0] ?? null
    setPending(next)

    const dialog = dialogRef.current
    if (!next && dialog?.open) {
      /* 與開啟端的 showModal fallback 對稱：環境（如 jsdom）缺 close() 時改移除 open 屬性 */
      if (typeof dialog.close === 'function') dialog.close()
      else dialog.removeAttribute('open')
    }
  }, [])

  return (
    <KiaoConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <dialog
          ref={dialogRef}
          data-kiao-confirm
          // Box values read the confirm roles in kiao-tokens.css (the App surface
          // re-values them). The backdrop stays a literal: ::backdrop only
          // inherits custom properties from Safari 17.4 / Chromium 122 on.
          className="m-auto w-[min(var(--measure-confirm),calc(100vw-32px))] rounded-[var(--radius-dialog)] border border-[color:var(--border-confirm)] bg-background px-[var(--inset-confirm)] pb-[var(--gap-md)] pt-[var(--inset-confirm)] text-foreground shadow-[var(--shadow-confirm)] backdrop:bg-black/40"
          aria-label={pending.options.title}
          /* Associate the message body so screen readers announce it as the
           * dialog's description (the native <dialog> keeps its implicit
           * `dialog` role — consumers query getByRole('dialog')). */
          aria-describedby={pending.options.message ? 'kiao-confirm-message' : undefined}
          onCancel={(event) => {
            event.preventDefault()
            settle(false)
          }}
          onKeyDownCapture={(event) => {
            // Enter/Escape can emit repeated keydown events while held. The
            // first may settle the current item; repeats must not act on the
            // next queued confirmation that takes its place.
            if (event.repeat && (event.key === 'Enter' || event.key === 'Escape')) {
              event.preventDefault()
              event.stopPropagation()
            }
          }}
          onClick={(event) => {
            /* 點背景 = 取消 */
            if (event.target === dialogRef.current) settle(false, event.detail)
          }}
        >
          <h3 className="text-[length:var(--fs-confirm-title)] font-semibold [overflow-wrap:anywhere]">
            {pending.options.title}
          </h3>
          {pending.options.message ? (
            <p
              className="mt-2 text-sm leading-[var(--leading-confirm-body)] text-muted [overflow-wrap:anywhere]"
              id="kiao-confirm-message"
            >
              {pending.options.message}
            </p>
          ) : null}
          <div className="mt-[var(--gap-md)] flex justify-end gap-[var(--gap-confirm-actions)]">
            <Button
              ref={cancelRef}
              variant="secondary"
              size={buttonSize}
              onClick={(event) => settle(false, event.detail)}
            >
              {pending.options.cancelText}
            </Button>
            <Button
              ref={confirmRef}
              variant={pending.options.tone === 'danger' ? 'danger' : 'primary'}
              size={buttonSize}
              onClick={(event) => settle(true, event.detail)}
            >
              {pending.options.confirmText}
            </Button>
          </div>
        </dialog>
      ) : null}
    </KiaoConfirmContext.Provider>
  )
}

export function useKiaoConfirm(): ConfirmFn {
  const confirm = useContext(KiaoConfirmContext)
  if (!confirm) {
    throw new Error('useKiaoConfirm must be used within a KiaoConfirmProvider')
  }
  return confirm
}
